import React from "react";
import { Box, Typography, Stack, alpha, useTheme, keyframes } from "@mui/material";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const drawLine = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const NotFound = ({
  message = "No results found",
  description = "",
  icon: Icon = SentimentDissatisfiedIcon,
  children,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 360,
        textAlign: "center",
        px: 3,
        py: 8,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "42%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            accent,
            0.05
          )} 0%, transparent 65%)`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 120,
          height: 120,
          borderRadius: "50%",
          mb: 3.5,
          background: `linear-gradient(145deg, ${alpha(
            accent,
            0.1
          )} 0%, ${alpha(accent, 0.02)} 100%)`,
          border: `1px solid ${alpha(accent, 0.1)}`,
          color: alpha(accent, 0.7),
          animation: `${fadeUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1)`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: -14,
            borderRadius: "50%",
            border: `1px dashed ${alpha(accent, 0.15)}`,
            animation: `spin 24s linear infinite`,
          },
          "@keyframes spin": {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
          },
        }}
      >
        <Icon sx={{ fontSize: 54 }} />
      </Box>

      <Typography
        sx={{
          position: "relative",
          zIndex: 1,
          fontFamily:
            "'Google Sans', 'Inter', 'Roboto', -apple-system, sans-serif",
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          fontWeight: 500,
          letterSpacing: "-0.015em",
          lineHeight: 1.3,
          color: "text.primary",
          mb: description ? 1.25 : 0,
          animation: `${fadeUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.05s both`,
        }}
      >
        {message}
      </Typography>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: 40,
          height: 2,
          borderRadius: 1,
          background: `linear-gradient(90deg, transparent 0%, ${alpha(
            accent,
            0.5
          )} 50%, transparent 100%)`,
          my: description ? 1.5 : 0,
          transformOrigin: "center",
          animation: `${drawLine} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both`,
          display: description ? "block" : "none",
        }}
      />

      {description && (
        <Typography
          sx={{
            position: "relative",
            zIndex: 1,
            fontFamily:
              "'Inter', 'Roboto', -apple-system, sans-serif",
            fontSize: "0.9375rem",
            fontWeight: 400,
            lineHeight: 1.6,
            color: "text.secondary",
            maxWidth: 440,
            animation: `${fadeUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both`,
          }}
        >
          {description}
        </Typography>
      )}

      {children && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            position: "relative",
            zIndex: 1,
            mt: 4,
            flexWrap: "wrap",
            justifyContent: "center",
            animation: `${fadeUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both`,
          }}
        >
          {children}
        </Stack>
      )}
    </Box>
  );
};

export default NotFound;